import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

/**
 * VoiceNotePlayer
 *
 * Compact play/pause + LIVE amplitude visualiser for voice notes. Uses
 * a Web Audio AnalyserNode to sample the audio spectrum in real time,
 * so the wave literally dances to the customer's / sender's voice
 * while playing. Falls back to a static pseudo-random envelope if the
 * browser can't wire up the analyser (e.g. Safari cross-origin decode
 * failures, missing WebAudio, or MoMo/opus codecs it can't decode).
 *
 * Used across every voice-note surface in the app — runner requests
 * (RideTrip, IncomingTripListener), pro booking requests (artisan
 * Bookings), and chat messages (customer/artisan Chat) — so the wave
 * looks and feels identical wherever it renders.
 *
 * Props
 *  src               — audio URL (already run through resolveMediaUrl)
 *  theme             — "dark" (default, coloured cards) | "light" (white cards)
 *  className         — extra classes for the outer wrapper
 *  testId            — data-testid root
 *  onError           — optional callback(err) when the <audio> element errors
 *  wrapperClass      — override background of the outer pill
 *  btnClass          — override play/pause button styling
 *  activeBarClass    — override the filled/playing bar colour
 *  inactiveBarClass  — override the unplayed bar colour
 *  timeClass         — override timestamp colour
 */
const WAVE_BARS = 28;

// Static fallback envelope — smooth sine + jitter so the wave doesn't
// look flat when we can't read live amplitude (Safari CORS decode,
// unsupported codec, or the pro simply hasn't hit play yet).
const IDLE_BARS = Array.from({ length: WAVE_BARS }, (_, i) => {
  const base = 0.35 + 0.55 * Math.abs(Math.sin(i * 0.55));
  return Math.max(0.18, Math.min(1, base));
});

const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
};

// Module-level active player tracker — tapping play on a new voice
// note auto-pauses whichever one is currently playing, matching
// WhatsApp / iMessage behaviour so users never end up with two
// clips talking over each other on a slow scroll.
let activePlayerAudio = null;

export default function VoiceNotePlayer({
  src,
  theme = "dark",
  className = "",
  testId = "voice-note-player",
  onError,
  wrapperClass,
  btnClass,
  activeBarClass,
  inactiveBarClass,
  timeClass,
}) {
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(null);
  const analyserFailedRef = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [bars, setBars] = useState(IDLE_BARS);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime || 0);
    const onLoaded = () => {
      setDuration(Number.isFinite(a.duration) ? a.duration : 0);
      setReady(true);
    };
    const onEnd = () => {
      setPlaying(false);
      setCurrent(0);
      a.currentTime = 0;
      stopTick();
      setBars(IDLE_BARS);
    };
    const onPause = () => {
      setPlaying(false);
      stopTick();
    };
    const onPlay = () => setPlaying(true);
    const onErr = (e) => {
      try { onError?.(a.error || e); } catch { /* noop */ }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    a.addEventListener("play", onPlay);
    a.addEventListener("error", onErr);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("error", onErr);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Pause and drop the analyser when unmounted so background rAFs
  // don't keep updating unmounted state on a fast route change.
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a && !a.paused) { try { a.pause(); } catch { /* noop */ } }
      stopTick();
      try { audioCtxRef.current?.close?.(); } catch { /* noop */ }
      audioCtxRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupAnalyser = () => {
    if (analyserFailedRef.current) return false;
    if (analyserRef.current) return true;
    const a = audioRef.current;
    if (!a) return false;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) throw new Error("no AudioContext");
      const ctx = new Ctx();
      // Attach the <audio> element as the source. If the audio is
      // cross-origin without the right CORS headers, Safari throws
      // a SecurityError here — we swallow it and fall back to the
      // idle envelope so playback still works, just without live
      // amplitude.
      const source = ctx.createMediaElementSource(a);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.55;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
      return true;
    } catch {
      analyserFailedRef.current = true;
      return false;
    }
  };

  const startTick = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      // Downsample the FFT bins into WAVE_BARS buckets. We skip the
      // very top of the spectrum where voice energy is negligible so
      // the bars respond more visibly to speech.
      const usable = Math.floor(data.length * 0.75);
      const step = usable / WAVE_BARS;
      const next = new Array(WAVE_BARS);
      for (let i = 0; i < WAVE_BARS; i++) {
        let sum = 0;
        let count = 0;
        const start = Math.floor(i * step);
        const end = Math.floor((i + 1) * step);
        for (let j = start; j < end; j++) { sum += data[j]; count++; }
        const avg = count > 0 ? sum / count : 0;
        // Normalise — voice content typically peaks around 180/255.
        next[i] = Math.min(1, Math.max(0.12, avg / 180));
      }
      setBars(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopTick = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (a.paused) {
        // Analyser must be built inside the user-gesture handler on
        // iOS / Chrome autoplay-blocked contexts; do it BEFORE calling
        // play() so the first frame already has data flowing.
        const ok = setupAnalyser();
        if (audioCtxRef.current?.state === "suspended") {
          try { await audioCtxRef.current.resume(); } catch { /* noop */ }
        }
        // Only one voice note plays at a time.
        if (activePlayerAudio && activePlayerAudio !== a) {
          try { activePlayerAudio.pause(); } catch { /* noop */ }
        }
        activePlayerAudio = a;
        await a.play();
        if (ok) startTick();
      } else {
        a.pause();
      }
    } catch { /* iOS autoplay / codec reject — ignore, error listener will fire */ }
  };

  const progress = duration > 0 ? Math.min(1, current / duration) : 0;
  const filled = Math.floor(progress * WAVE_BARS);

  const dark = theme === "dark";
  const defaultBtn = dark
    ? "bg-[#5B21B6] hover:bg-[#4C1D95] text-white shadow-md"
    : "bg-[#4F988E] hover:bg-[#3d7a72] text-white";
  const defaultWrap = dark ? "bg-white/10" : "bg-gray-100";
  const defaultTime = dark ? "text-white/90" : "text-gray-600";
  const defaultActive = dark ? "bg-white" : "bg-[#4F988E]";
  const defaultInactive = dark ? "bg-white/40" : "bg-gray-300";

  return (
    <div
      className={`flex items-center gap-3 rounded-full px-3 py-2 ${wrapperClass || defaultWrap} ${className}`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition ${btnClass || defaultBtn}`}
        data-testid={`${testId}-toggle`}
      >
        {playing ? <Pause size={16} fill="currentColor" strokeWidth={0} /> : <Play size={16} fill="currentColor" strokeWidth={0} className="ml-0.5" />}
      </button>
      <div className="flex-1 h-8 flex items-center gap-[3px] overflow-hidden">
        {bars.map((h, i) => {
          const active = i < filled;
          // Use live amplitude when playing, idle envelope when paused.
          const displayH = playing ? h : IDLE_BARS[i];
          return (
            <span
              key={i}
              className={`w-[3px] rounded-full ${active ? (activeBarClass || defaultActive) : (inactiveBarClass || defaultInactive)}`}
              style={{
                height: `${Math.max(15, displayH * 100)}%`,
                transition: playing ? "height 90ms linear, background-color 120ms" : "background-color 120ms",
              }}
            />
          );
        })}
      </div>
      <span className={`text-[11px] font-medium tabular-nums shrink-0 ${timeClass || defaultTime}`}>
        {ready ? fmt(playing ? current : (current || duration)) : "0:00"}
      </span>
      {/* crossOrigin="anonymous" lets the AnalyserNode read cross-origin
          audio when the backend serves the file with CORS headers.
          Without it Safari raises SecurityError and we fall back to
          the idle envelope — playback still works either way. */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        playsInline
        crossOrigin="anonymous"
        className="hidden"
      />
    </div>
  );
}
